import { IssueLiabilityResponsibleType } from '@gooduncles/gu-app-schema';
import { ICellRendererParams } from 'ag-grid-community';
import { FC, useEffect } from 'react';

import { selectPartnersUserById } from 'src/redux/slices/partners';
import { useAppSelector } from 'src/redux/store';

import { formatDisplayName } from 'src/hooks/subscription/useStaffUsers';
import useSWRProductSupplier from 'src/hooks/swr/useSWRProductSupplier';
import useSWRStore from 'src/hooks/swr/useSWRStore';
import useSWRUser from 'src/hooks/swr/useSWRUser';

interface AgCellIssueLiabilityResponsibleDisplayProps extends Partial<ICellRendererParams> {
  responsibleType?: IssueLiabilityResponsibleType;
  responsibleId?: string;
}

interface ResponsibleDisplayProps {
  id: string;
  setValue?: (value: string) => void;
}

/** AG-Grid setValue와 동기화하여 정렬/필터에 표시값을 반영한다. */
function useSyncSetValue(setValue: ((value: string) => void) | undefined, value: string): void {
  useEffect(() => {
    setValue?.(value);
  }, [setValue, value]);
}

const StoreResponsibleDisplay: FC<ResponsibleDisplayProps> = ({ id, setValue }) => {
  const { store } = useSWRStore(id);
  const value = store?.storeNickname ?? store?.storeName ?? '-';
  useSyncSetValue(setValue, value);
  return <span>{value}</span>;
};

const UserResponsibleDisplay: FC<ResponsibleDisplayProps> = ({ id, setValue }) => {
  const { user } = useSWRUser(id);
  const value = formatDisplayName(user);
  useSyncSetValue(setValue, value);
  return <span>{value}</span>;
};

const PartnerResponsibleDisplay: FC<ResponsibleDisplayProps> = ({ id, setValue }) => {
  const partner = useAppSelector((state) => selectPartnersUserById(state, id));
  const value = partner?.nickname ?? '-';
  useSyncSetValue(setValue, value);
  return <span>{value}</span>;
};

const SupplierResponsibleDisplay: FC<ResponsibleDisplayProps> = ({ id, setValue }) => {
  const { supplier } = useSWRProductSupplier(id);
  const value = supplier?.name ?? '-';
  useSyncSetValue(setValue, value);
  return <span>{value}</span>;
};

export const AgCellIssueLiabilityResponsibleDisplay: FC<AgCellIssueLiabilityResponsibleDisplayProps> = ({
  responsibleType: responsibleTypeProp,
  responsibleId: responsibleIdProp,
  data,
  setValue,
}) => {
  const responsibleType = responsibleTypeProp ?? data?.responsibleType;
  const responsibleId = responsibleIdProp ?? data?.responsibleId;

  if (!responsibleType || !responsibleId) return <span>-</span>;

  switch (responsibleType) {
    case 'store':
      return <StoreResponsibleDisplay id={responsibleId} setValue={setValue} />;
    case 'operator':
      return <UserResponsibleDisplay id={responsibleId} setValue={setValue} />;
    case 'partners':
      return <PartnerResponsibleDisplay id={responsibleId} setValue={setValue} />;
    case 'productSupplier':
      return <SupplierResponsibleDisplay id={responsibleId} setValue={setValue} />;
    case 'etc':
      return <span>{responsibleId}</span>;
    default:
      return <span>-</span>;
  }
};
